import { useEffect, useState } from 'react';
import { Button, Input, Select, DatePicker } from '@scuf/common';
import { useTranslation } from 'react-i18next';
import { FilterReportDTO, Part, Product, ReportDTO, SelectOption, StatusOption } from 'models/hsl/general';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { hslCountriesAtom, hslPartnersAtom } from 'resources/atoms/hslAtom';
import TableReport from 'pages/hsl/report/tableReport';
import { HSL, notificationAtom } from 'resources';
import useFetch from 'helpers/hooks/useFetch';

type ReportName =
    | 'Contracts'
    | 'Equipments'
    | 'Deliveries'
    | 'ItemsInventories'
    | 'Parts'
    | 'ProductParts'
    | 'PendingItemsInventories'
    | 'Products'
    | 'Quotation'
    | 'RepairParts'
    | 'Repair'
    | 'BounceRate'
    | 'OTTR'
    | 'MCPR'
    | 'Inventory'
    | 'IdStatus'
    | 'InventoryTotalPrice';

const InitialFilter: FilterReportDTO = {
    TypeReport: null,
    DateStart: null,
    DateEnd: null,
    IdRepair: null,
    StatusRepair: '',
    TypeService: '',
    Company: '',
    Customer: '',
    User: '',
    Technical: '',
    Partner: '',
    PartnerReceived: '',
    Country: '',
    SaleDate: null,
    Contract: '',
    StatusContract: '',
    Warranty: '',
    Product: '',
    ProductDescription: '',
    PartNumber: '',
    PartNumberDescription: '',
    SerialNumber: '',
    AddOn: '',
    DeliveryNumber: '',
    BillofLading: '',
    Carrier: '',
    Action: '',
    IdInventory: null,
    IdItemPending: null,
    Pending: '',
    BounceRateDays: null,
    SLA: '',
    MCPR: '',
    EndDate: null,
    PageIndex: 1,
    PageSize: 10,
    CreatedAt: null,
    UpdatedAt: null
};

function ReportPage() {
    const { t } = useTranslation(['common', 'hsl', 'user', 'home', 'notification']);
    const [filters, setFilters] = useState(InitialFilter);
    const setNotification = useSetRecoilState(notificationAtom);
    const [loading, setLoading] = useState(false);

    const [countries] = useRecoilState(hslCountriesAtom);
    const [partners] = useRecoilState(hslPartnersAtom);
    const [parts, setParts] = useState<SelectOption[]>([]);
    const [products, setProducts] = useState<SelectOption[]>([]);

    const [report, setReport] = useState<ReportName | null>(null);
    const [reportData, setReportData] = useState<ReportDTO | null>(null);
    const [reportItems, setReportItems] = useState<number>(0);
    const [resetKey, setResetKey] = useState(0);

    const [, getParts] = useFetch<null | Part[]>({
        url: HSL.getParts,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    const [, getProducts] = useFetch<null | Product[]>({
        url: HSL.getProducts,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    const [, getReport] = useFetch<null | FilterReportDTO, null | ReportDTO>({
        url: HSL.getReport,
        method: 'POST',
        onError: t('common:errors.Unexpected')
    });

    interface IOption {
        text: string;
        value: ReportName;
    }
    const options: IOption[] = [
        { text: t('hsl:translation.Contracts'), value: 'Contracts' },
        { text: t('translation.Equipments'), value: 'Equipments' },
        { text: t('hsl:translation.Deliveries'), value: 'Deliveries' },
        { text: t('hsl:translation.ItemsInventories'), value: 'ItemsInventories' },
        { text: t('hsl:translation.Parts'), value: 'Parts' },
        { text: t('hsl:translation.ProductParts'), value: 'ProductParts' },
        { text: t('hsl:translation.PendingItemsInventories'), value: 'PendingItemsInventories' },
        { text: t('translation.Products'), value: 'Products' },
        { text: t('hsl:translation.Quotation'), value: 'Quotation' },
        { text: t('hsl:translation.RepairParts'), value: 'RepairParts' },
        { text: t('hsl:translation.Repairs'), value: 'Repair' },
        { text: t('hsl:translation.BounceRate'), value: 'BounceRate' },
        { text: 'OTTR', value: 'OTTR' },
        { text: 'MCPR', value: 'MCPR' },
        { text: t('hsl:translation.Inventory'), value: 'Inventory' },
        { text: t('ID Status'), value: 'IdStatus' },
        { text: t('Inventory Total Price'), value: 'InventoryTotalPrice' }
    ];

    const servicesType = [
        { value: 'Contract', text: t('hsl:translation.Contract') },
        { value: 'Warranty', text: t('hsl:translation.Warranty') }
    ];

    const yesOrNo = [
        { value: 'true', text: t('common:actions.Yes') },
        { value: 'false', text: t('common:actions.No') }
    ];

    const bounceRate = [
        { value: 30, text: t('30') },
        { value: 60, text: t('60') },
        { value: 90, text: t('90') }
    ];

    const OTTR = [
        { value: 'Out Time', text: t('Out Time') },
        { value: 'On Time', text: t('On Time') }
    ];

    const statusOptions: StatusOption[] = [
        { value: 'Equipment Entered for Repair', text: t('hsl:translation.EnteredRepair'), color: 'yellow' },
        { value: 'In Diagnosis', text: t('hsl:translation.Diagnosis'), color: 'grey' },
        { value: 'In the process of Repair', text: t('hsl:translation.Repairing'), color: 'blue' },
        { value: 'Waiting for Parts', text: t('hsl:translation.WaitingParts'), color: 'red' },
        { value: 'Sent/Delivered to the customer', text: t('hsl:translation.SentCustomer'), color: 'green' }
    ];

    const statusType: SelectOption[] = [
        { value: 'Active', text: t('hsl:translation.Active') },
        { value: 'Inactive', text: t('hsl:translation.Inactive') }
    ];

    const isActive = (...names: ReportName[]) => {
        return report !== null && names.includes(report);
    };

    const formatDate = (value?: string | Date) => {
        if (!value) return value;

        const iso = value instanceof Date ? value.toISOString() : typeof value === 'string' ? value : null;

        if (!iso || !iso.includes('T')) return value;

        const dateOnly = iso.split('T')[0]; // yyyy-MM-dd
        const [year, month, day] = dateOnly.split('-');

        return `${day}/${month}/${year}`;
    };

    const normalizeDateFields = (item: Record<string, any>) => ({
        ...item,
        EndDate: formatDate(item.EndDate),
        StartDate: formatDate(item.StartDate),
        SaleDate: formatDate(item.SaleDate)
    });

    const handleApply = async () => {
        setReportData(null);
        if (filters.TypeReport === null) {
            setNotification({ type: 'critical', message: 'Selecione um tipo de relatório.' });
            return;
        }
        if (
            !isActive('Parts', 'ProductParts', 'InventoryTotalPrice', 'MCPR') &&
            (filters.DateStart === null || filters.DateEnd === null)
        ) {
            setNotification({ type: 'critical', message: 'Selecione um período.' });
            return;
        }
        setLoading(true);
        setNotification({ type: 'information', message: 'Buscando dados. Por favor, aguarde.' });
        let nextFilters = { ...filters };
        if (
            isActive('Parts', 'ProductParts', 'InventoryTotalPrice', 'MCPR') &&
            filters.DateStart === null &&
            filters.DateEnd === null
        ) {
            const dateStart = new Date(Date.UTC(2000, 0, 1, 0, 0, 0, 0));
            const dateEnd = new Date();

            nextFilters = { ...nextFilters, DateStart: dateStart, DateEnd: dateEnd };
            setFilters(nextFilters);
        }
        try {
            console.log(nextFilters);
            await getReport(nextFilters, (response: any) => {
                if (!response || !response.data) {
                    setNotification({ type: 'critical', message: 'Nenhum dado retornado.' });
                } else {
                    const result = response.data;
                    console.log(result);
                    if (result.TotalCount > 0) {
                        const normalizedData = result.Data.map((item: any) => normalizeDateFields(item));

                        setReportData(normalizedData);
                        setReportItems(result.TotalCount);
                        setNotification({ type: 'success', message: 'Dados retornados.' });
                    } else {
                        setNotification({ type: 'critical', message: 'Nenhum dado retornado.' });
                        return;
                    }
                }
            });
        } catch (err) {
            console.error('Falha ao obter relatório:', err);
            setNotification({ type: 'critical', message: 'Erro ao buscar dados.' });
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = async (pageNumber: number) => {
        const filterPage: FilterReportDTO = { ...filters, PageIndex: Number(pageNumber) };

        setLoading(true);
        setNotification({ type: 'information', message: 'Buscando dados. Por favor, aguarde.' });

        try {
            await getReport(filterPage, (response: any) => {
                const result = response.data;

                const normalizedData = result.Data.map((item: any) => normalizeDateFields(item));

                setReportData(normalizedData);
                setReportItems(result.TotalCount);
            });
        } catch {
            setNotification({ type: 'critical', message: 'Erro ao buscar dados.' });
        } finally {
            setLoading(false);
        }
    };

    function handleReset() {
        setReportData(null);
        setReport(null);
        setFilters({ ...InitialFilter });
        setResetKey(k => k + 1);
    }

    useEffect(() => {
        if (!products?.length) {
            loadProducts();
        }
        if (!parts.length) {
            loadParts();
        }
    }, [report]);

    const loadParts = async () => {
        await getParts(null, (current: any) => {
            const data: Part[] = current?.data || [];
            const options = data.map(part => ({
                text: part.PartNumber || '',
                value: part.PartNumber || ''
            }));
            setParts(options);
        });
    };

    const loadProducts = async () => {
        await getProducts(null, (current: any) => {
            const data: Product[] = current?.data || [];
            const options = data.map(product => ({
                text: product.Name,
                value: product.Name
            }));
            setProducts(options);
        });
    };

    const SearchReport = () => {
        return (
            <div>
                <div className="form4Columns">
                    <Select
                        key={`type-${resetKey}`}
                        indicator="required"
                        className="SelectStyle"
                        label={t('common:translation.Type')}
                        placeholder={t('common:translation.Type')}
                        options={options}
                        value={report ?? undefined}
                        onChange={(value: ReportName | null) => {
                            setReport(value ?? null);
                            setFilters(prev => ({ ...prev, TypeReport: value ?? null }));
                        }}
                    />
                    {!isActive('InventoryTotalPrice') && (
                        <DatePicker
                            key={`date-${resetKey}`}
                            type="daterange"
                            indicator={isActive('Parts', 'ProductParts', 'MCPR') ? 'optional' : 'required'}
                            label={
                                report == 'Repair' || report == 'IdStatus'
                                    ? t('hsl:translation.TerminoDate')
                                    : report == 'BounceRate'
                                      ? t('hsl:translation.StartDate')
                                      : t('common:translation.Period')
                            }
                            rangeValue={
                                filters.DateStart && filters.DateEnd
                                    ? {
                                          from: new Date(
                                              filters.DateStart.getUTCFullYear(),
                                              filters.DateStart.getUTCMonth(),
                                              filters.DateStart.getUTCDate()
                                          ),
                                          to: new Date(
                                              filters.DateEnd.getUTCFullYear(),
                                              filters.DateEnd.getUTCMonth(),
                                              filters.DateEnd.getUTCDate()
                                          )
                                      }
                                    : undefined
                            }
                            displayFormat={'DD/MM/YYYY'}
                            closeOnSelection={false}
                            closeOnDocumentClick={true}
                            onRangeSelect={({ from, to }: { from?: Date; to?: Date }) => {
                                if (!from || !to) {
                                    setFilters({ ...filters, DateStart: null, DateEnd: null });
                                    return;
                                }

                                const fromStr = from.toISOString().split('T')[0];
                                const toStr = to.toISOString().split('T')[0];

                                const start = new Date(`${fromStr}T00:00:00.000Z`);
                                const end = new Date(`${toStr}T23:59:59.999Z`);

                                setFilters({ ...filters, DateStart: start, DateEnd: end });
                            }}
                        />
                    )}
                </div>
            </div>
        );
    };

    const FiltersReport = () => {
        return (
            <div>
                <div className="form4Columns">
                    {isActive('RepairParts', 'Repair', 'IdStatus') && (
                        <Input
                            placeholder={t('hsl:translation.IdRepair')}
                            label={t('hsl:translation.IdRepair')}
                            type="number"
                            value={filters.IdRepair?.toString()}
                            onChange={old => setFilters({ ...filters, IdRepair: parseInt(old) })}
                        />
                    )}
                    {isActive('Repair', 'Inventory', 'IdStatus', 'RepairParts') && (
                        <Select
                            placeholder={t('hsl:translation.StatusRepair')}
                            label={t('hsl:translation.StatusRepair')}
                            options={statusOptions}
                            value={filters.StatusRepair}
                            search={true}
                            onChange={old => setFilters({ ...filters, StatusRepair: old })}
                        />
                    )}
                    {isActive('Contracts', 'Repair', 'BounceRate', 'OTTR', 'Inventory', 'IdStatus') && (
                        <Select
                            placeholder={t('hsl:translation.TypeServices')}
                            label={t('hsl:translation.TypeServices')}
                            value={filters.TypeService}
                            options={servicesType}
                            search={true}
                            onChange={old => setFilters({ ...filters, TypeService: old })}
                        />
                    )}
                    {isActive('Contracts', 'RepairParts', 'Repair', 'OTTR', 'Inventory', 'IdStatus') && (
                        <Input
                            placeholder={t('hsl:translation.Customer')}
                            label={t('hsl:translation.Customer')}
                            value={filters.Customer}
                            onChange={old => setFilters({ ...filters, Customer: old })}
                        />
                    )}
                    {isActive('Repair', 'IdStatus') && (
                        <Input
                            placeholder={t('hsl:translation.User')}
                            label={t('hsl:translation.User')}
                            value={filters.User}
                            onChange={old => setFilters({ ...filters, User: old })}
                        />
                    )}
                    {isActive('Repair', 'IdStatus') && (
                        <Input
                            placeholder={t('hsl:translation.Technical')}
                            label={t('hsl:translation.Technical')}
                            value={filters.Technical}
                            onChange={old => setFilters({ ...filters, Technical: old })}
                        />
                    )}
                    {isActive(
                        'Contracts',
                        'Deliveries',
                        'Quotation',
                        'Equipments',
                        'ItemsInventories',
                        'Inventory',
                        'RepairParts',
                        'PendingItemsInventories',
                        'Repair',
                        'IdStatus',
                        'InventoryTotalPrice',
                        'MCPR'
                    ) && (
                        <Select
                            placeholder={t('common:translation.Partner')}
                            label={t('common:translation.Partner')}
                            options={partners}
                            value={filters.Partner}
                            search={true}
                            onChange={old => setFilters({ ...filters, Partner: old })}
                        />
                    )}
                    {isActive('Deliveries') && (
                        <Select
                            placeholder={t('hsl:translation.PartnerReceived')}
                            label={t('hsl:translation.PartnerReceived')}
                            options={yesOrNo}
                            value={filters.PartnerReceived}
                            onChange={old => setFilters({ ...filters, PartnerReceived: old })}
                        />
                    )}
                    {isActive('Contracts', 'Equipments', 'Repair', 'OTTR', 'Inventory', 'IdStatus') && (
                        <Input
                            placeholder={t('hsl:translation.Contract')}
                            label={t('hsl:translation.Contract')}
                            value={filters.Contract}
                            onChange={old => setFilters({ ...filters, Contract: old })}
                        />
                    )}
                    {isActive('Contracts') && (
                        <Select
                            placeholder={t('hsl:translation.StatusContract')}
                            label={t('hsl:translation.StatusContract')}
                            options={statusType}
                            value={filters.StatusContract}
                            onChange={old => setFilters({ ...filters, StatusContract: old })}
                        />
                    )}
                    {isActive('Contracts') && (
                        <Input
                            placeholder={t('hsl:translation.Warranty')}
                            label={t('hsl:translation.Warranty')}
                            value={filters.Warranty}
                            onChange={old => setFilters({ ...filters, Warranty: old })}
                        />
                    )}
                    {isActive(
                        'Equipments',
                        'Deliveries',
                        'Contracts',
                        'ItemsInventories',
                        'Parts',
                        'PendingItemsInventories',
                        'Quotation',
                        'ProductParts',
                        'Repair',
                        'OTTR',
                        'Inventory',
                        'IdStatus',
                        'InventoryTotalPrice'
                    ) && (
                        <Select
                            placeholder={t('common:translation.PartNumber')}
                            label={t('common:translation.PartNumber')}
                            value={filters.PartNumber}
                            options={parts}
                            search={true}
                            onChange={old => setFilters({ ...filters, PartNumber: old })}
                        />
                    )}
                    {isActive('Parts', 'InventoryTotalPrice') && (
                        <Input
                            placeholder={t('hsl:translation.PartDescription')}
                            label={t('hsl:translation.PartDescription')}
                            value={filters.PartNumberDescription}
                            onChange={old => setFilters({ ...filters, PartNumberDescription: old })}
                        />
                    )}
                    {isActive('Parts', 'ProductParts', 'Products', 'InventoryTotalPrice') && (
                        <Select
                            placeholder={t('common:translation.Product')}
                            label={t('common:translation.Product')}
                            options={products}
                            search={true}
                            onChange={old => setFilters({ ...filters, Product: old })}
                        />
                    )}
                    {isActive('ProductParts', 'Products') && (
                        <Input
                            placeholder={t('hsl:translation.ProductDescription')}
                            label={t('hsl:translation.ProductDescription')}
                            value={filters.ProductDescription}
                            onChange={old => setFilters({ ...filters, ProductDescription: old })}
                        />
                    )}
                    {isActive('Equipments', 'RepairParts', 'Repair', 'IdStatus') && (
                        <Input
                            placeholder={t('common:translation.SerialNumber')}
                            label={t('common:translation.SerialNumber')}
                            value={filters.SerialNumber}
                            onChange={old => setFilters({ ...filters, SerialNumber: old })}
                        />
                    )}
                    {isActive('Contracts') && (
                        <DatePicker
                            label={t('hsl:translation.SaleDate')}
                            reserveSpace={false}
                            value={filters.SaleDate?.toString()}
                            onChange={old => setFilters({ ...filters, SaleDate: old })}
                            displayFormat={'DD/MM/YYYY'}
                        />
                    )}
                    {isActive(
                        'Contracts',
                        'Deliveries',
                        'ItemsInventories',
                        'PendingItemsInventories',
                        'Repair',
                        'RepairParts',
                        'BounceRate',
                        'OTTR',
                        'MCPR',
                        'Inventory',
                        'Quotation',
                        'IdStatus',
                        'InventoryTotalPrice'
                    ) && (
                        <Select
                            placeholder={t('user:address.Country')}
                            label={t('user:address.Country')}
                            value={filters.Country}
                            options={countries}
                            search={true}
                            onChange={old => setFilters({ ...filters, Country: old })}
                        />
                    )}
                    {isActive('Deliveries') && (
                        <Input
                            label={t('hsl:translation.IdDelivery')}
                            placeholder={t('hsl:translation.IdDelivery')}
                            type="number"
                            value={filters.DeliveryNumber}
                            onChange={old => setFilters({ ...filters, DeliveryNumber: old })}
                        />
                    )}
                    {isActive('Deliveries') && (
                        <Input
                            label={t('hsl:translation.BillofLading')}
                            placeholder={t('hsl:translation.BillofLading')}
                            value={filters.BillofLading}
                            onChange={old => setFilters({ ...filters, BillofLading: old })}
                        />
                    )}
                    {isActive('Deliveries') && (
                        <Input
                            label={t('hsl:translation.Carrier')}
                            placeholder={t('hsl:translation.Carrier')}
                            value={filters.Carrier}
                            onChange={old => setFilters({ ...filters, Carrier: old })}
                        />
                    )}
                    {isActive('Contracts', 'Equipments') && (
                        <Input
                            placeholder={t('hsl:translation.AddOn')}
                            label={t('hsl:translation.AddOn')}
                            value={filters.AddOn}
                            onChange={old => setFilters({ ...filters, AddOn: old })}
                        />
                    )}
                    {isActive() && (
                        <Input
                            placeholder={t('hsl:translation.IdItemInventory')}
                            label={t('hsl:translation.IdItemInventory')}
                            type="number"
                            value={filters.IdInventory?.toString()}
                            onChange={old => setFilters({ ...filters, IdInventory: parseInt(old) })}
                        />
                    )}
                    {isActive() && (
                        <Input
                            placeholder={t('hsl:translation.IdPendingItemInventory')}
                            label={t('hsl:translation.IdPendingItemInventory')}
                            type="number"
                            value={filters.IdItemPending?.toString()}
                            onChange={old => setFilters({ ...filters, IdItemPending: parseInt(old) })}
                        />
                    )}
                    {isActive('RepairParts') && (
                        <Select
                            placeholder={t('hsl:translation.Pending')}
                            label={t('hsl:translation.Pending')}
                            options={yesOrNo}
                            value={filters.Pending}
                            onChange={old => setFilters({ ...filters, Pending: old })}
                        />
                    )}
                    {isActive('BounceRate') && (
                        <Select
                            indicator="required"
                            placeholder={t('hsl:translation.BounceRateDays')}
                            label={t('hsl:translation.BounceRateDays')}
                            options={bounceRate}
                            value={filters.BounceRateDays}
                            onChange={old => setFilters({ ...filters, BounceRateDays: old })}
                        />
                    )}
                    {isActive('OTTR') && (
                        <Select
                            placeholder={t('OTTR')}
                            label={t('OTTR')}
                            options={OTTR}
                            value={filters.SLA}
                            onChange={old => setFilters({ ...filters, SLA: old })}
                        />
                    )}
                    {isActive('Repair') && (
                        <DatePicker
                            key={`date-${resetKey}`}
                            type="daterange"
                            label={t('common:translation.DateCreate')}
                            rangeValue={
                                filters.RangeStartDateCreateRepair && filters.RangeEndDateCreateRepair
                                    ? {
                                          from: new Date(
                                              filters.RangeStartDateCreateRepair.getUTCFullYear(),
                                              filters.RangeStartDateCreateRepair.getUTCMonth(),
                                              filters.RangeStartDateCreateRepair.getUTCDate()
                                          ),
                                          to: new Date(
                                              filters.RangeEndDateCreateRepair.getUTCFullYear(),
                                              filters.RangeEndDateCreateRepair.getUTCMonth(),
                                              filters.RangeEndDateCreateRepair.getUTCDate()
                                          )
                                      }
                                    : undefined
                            }
                            displayFormat={'DD/MM/YYYY'}
                            closeOnSelection={false}
                            closeOnDocumentClick={true}
                            onRangeSelect={({ from, to }: { from?: Date; to?: Date }) => {
                                if (!from || !to) {
                                    setFilters({ ...filters, RangeStartDateCreateRepair: null, RangeEndDateCreateRepair: null });
                                    return;
                                }

                                const fromStr = from.toISOString().split('T')[0];
                                const toStr = to.toISOString().split('T')[0];

                                const start = new Date(`${fromStr}T00:00:00.000Z`);
                                const end = new Date(`${toStr}T23:59:59.999Z`);

                                setFilters({ ...filters, RangeStartDateCreateRepair: start, RangeEndDateCreateRepair: end });
                            }}
                        />
                    )}
                </div>

                {filters.TypeReport && (
                    <div className="buttonsSearch">
                        <Button
                            type="secondary"
                            icon="badge-delete"
                            content={t('common:actions.ResetFilters')}
                            iconPosition="right"
                            onClick={handleReset}
                            loading={loading}
                            disabled={loading}
                        />
                        <Button
                            type="primary"
                            disabled={
                                isActive('Parts', 'ProductParts', 'InventoryTotalPrice', 'MCPR')
                                    ? loading
                                    : isActive('BounceRate')
                                      ? !filters.BounceRateDays || loading
                                      : !filters.DateStart || !filters.DateEnd || loading
                            }
                            icon="filter1"
                            content={t('common:actions.ApplyFilters')}
                            iconPosition="right"
                            onClick={handleApply}
                            loading={loading}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {SearchReport()}
            {FiltersReport()}
            <TableReport
                reportType={report}
                reportData={reportData}
                reportFilter={filters}
                reportItems={reportItems}
                reportLoading={loading}
                onPageChange={handlePageChange}
            />
        </>
    );
}

export default ReportPage;
